import { useState } from "react";
import { notifications } from "@mantine/notifications";
import {
  ItemTypeEnum,
  MaterialCharacteristics,
  ProductTypeEnum,
} from "@jasper/client-models";
import {
  itemPreferencesToInput,
  type useOrderConfiguration,
} from "@jasper/use-cases";
import { SplintableCrown } from "./splinting.utils";

type OrderConfiguration = ReturnType<typeof useOrderConfiguration>;

// What a crown was before being splinted, to recreate it as it was
type CrownSnapshot = {
  tooth: number;
  materialCharacteristics: MaterialCharacteristics;
  itemPreferences: ReturnType<typeof itemPreferencesToInput>;
};

const toCrownProduct = ({ tooth, materialCharacteristics }: CrownSnapshot) => ({
  type: ProductTypeEnum.crown,
  teeth: [tooth],
  items: [{ tooth, type: ItemTypeEnum.crown, materialCharacteristics }],
});

// Merges adjacent crowns into one splinted crown, and undoes the last merge while the page is open
export const useCrownSplinting = ({
  order,
  updateProduct,
  updateProducts,
  addOrderProducts,
  removeOrderProducts,
}: Pick<
  OrderConfiguration,
  | "order"
  | "updateProduct"
  | "updateProducts"
  | "addOrderProducts"
  | "removeOrderProducts"
>) => {
  // The last splinted crown and the crowns it was made of. so the undo only applies
  //to a merge just made here, never to a splinted crown configured in the order flow.
  const [lastSplint, setLastSplint] = useState<{
    productId: string;
    crowns: CrownSnapshot[];
  }>();

  const getItemPreferences = (productId: string) =>
    itemPreferencesToInput(
      order?.products?.find(product => product.id === productId)
        ?.itemPreferences
    );

  // The first crown becomes a SPLINTED_CROWN carrying every tooth (each keeping its own material
  // characteristics, and the first crown's preferences), then the others are removed.
  const splintCrowns = async ([
    firstCrown,
    ...otherCrowns
  ]: SplintableCrown[]) => {
    const crowns = [firstCrown, ...otherCrowns];
    const snapshots = crowns.map(crown => ({
      tooth: crown.tooth,
      materialCharacteristics: crown.materialCharacteristics,
      itemPreferences: getItemPreferences(crown.product.id),
    }));

    try {
      await updateProduct({
        productId: firstCrown.product.id,
        type: ProductTypeEnum.splintedCrown,
        teeth: crowns.map(crown => crown.tooth),
        items: crowns.map(crown => ({
          tooth: crown.tooth,
          type: ItemTypeEnum.crown,
          materialCharacteristics: crown.materialCharacteristics,
        })),
        itemPreferences: snapshots[0].itemPreferences,
      });
    } catch (error) {
      console.error("Error while splinting crowns", error);
      notifications.show({
        color: "red",
        title: "Solidarisation impossible",
        message:
          "Les couronnes n'ont pas pu être solidarisées. Veuillez réessayer.",
      });
      return;
    }

    try {
      await removeOrderProducts(otherCrowns.map(crown => crown.product.id));
    } catch (error) {
      console.error("Error while removing the splinted crowns", error);
      notifications.show({
        color: "red",
        title: "Solidarisation incomplète",
        message:
          "La couronne solidarisée a été créée, mais les couronnes d'origine n'ont pas pu être supprimées. Supprimez-les depuis la liste des prothèses.",
      });
      // The original crowns are still there: undoing would duplicate them
      return;
    }

    setLastSplint({ productId: firstCrown.product.id, crowns: snapshots });
  };

  // The reverse of splintCrowns: the splinted crown goes back to the first crown, the others are
  // recreated, then given back their preferences (a created product takes none).
  const undoLastSplint = async () => {
    if (!lastSplint) {
      return;
    }
    const [firstCrown, ...otherCrowns] = lastSplint.crowns;

    try {
      await updateProduct({
        productId: lastSplint.productId,
        ...toCrownProduct(firstCrown),
        itemPreferences: firstCrown.itemPreferences,
      });
    } catch (error) {
      console.error("Error while undoing the splint", error);
      notifications.show({
        color: "red",
        title: "Annulation impossible",
        message:
          "La solidarisation n'a pas pu être annulée. Veuillez réessayer.",
      });
      return;
    }

    setLastSplint(undefined);

    try {
      const productIdsBefore = new Set(
        order?.products?.map(product => product.id)
      );
      const { data } = await addOrderProducts(otherCrowns.map(toCrownProduct));
      const createdProducts =
        data?.addOrderProducts.products.filter(
          product => !productIdsBefore.has(product.id)
        ) ?? [];

      const preferencesToRestore = otherCrowns.flatMap(crown => {
        const createdProduct = createdProducts.find(
          product =>
            product.teeth.length === 1 && product.teeth[0] === crown.tooth
        );

        return createdProduct && crown.itemPreferences
          ? [
              {
                productId: createdProduct.id,
                itemPreferences: crown.itemPreferences,
              },
            ]
          : [];
      });

      if (preferencesToRestore.length) {
        await updateProducts(preferencesToRestore);
      }
    } catch (error) {
      console.error("Error while recreating the unsplinted crowns", error);
      notifications.show({
        color: "red",
        title: "Annulation incomplète",
        message:
          "La solidarisation a été annulée, mais les couronnes d'origine n'ont pas toutes pu être recréées à l'identique. Vérifiez la liste des prothèses.",
      });
    }
  };

  return {
    splintCrowns,
    lastSplintedProductId: lastSplint?.productId,
    undoLastSplint,
  };
};
